
import Vue from "vue";
import SelectBox from "@/components/molecules/SelectBox.vue";
import MultipleSelectBox from "@/components/molecules/MultipleSelectBox.vue";
import CupertinoAlertDialog, { CupertinoAlertDialogProps } from "@/components/ui/dialogs/CupertinoAlertDialog.vue";
import { genre as genreList, category as categoryList } from "@/lib/novelSettings";
import {
  getTranslatedGenreList,
  getTranslatedCategoryList,
} from "@/lib/utils/novelSettingsHelper";
import { Novel, NovelSetting } from "@/lib/models";
import { VueLoading } from "vue-loading-template";
import { Dialog } from "@/lib/utils";

interface Data {
  title: string;
  genre: NovelSetting | string | null;
  categories: NovelSetting[] | string[] | null;
  wordLength: number | null;
  genreList: NovelSetting[];
  categoryList: NovelSetting[];
  isCreating: boolean;
}

interface Methods {
  onCreateButtonClick: () => void;
  onSelectGenre: (item: NovelSetting) => void;
  onUnSelectGenre: () => void;
  onSelectCategories: (items: NovelSetting[]) => void;

  createNovel: (isSkipStart?: boolean) => void;
  createManuscript: (novelId: String) => void;
  onSkipStart: () => void;
}

interface Computed {
  isValidWordLength: boolean;
  canSubmit: boolean;
  translatedGenreList: NovelSetting[];
  translatedCategoryList: NovelSetting[];
}

export default Vue.extend<Data, Methods, Computed, {}>({
  components: {
    SelectBox,
    MultipleSelectBox,
    VueLoading,
  },

  mounted() {
    // 画面サイズが大きい場合に、画面下部の背景色が白くならないように
    // コンポーネントが表示されたときに背景色を変更
    document.getElementById("app")?.style.setProperty("background-color", "#ededed");
  },
  beforeDestroy() {
    // 他のコンポーネントに影響しないように、コンポーネントが破棄されるときに背景色を元に戻す
    document.getElementById("app")?.style.removeProperty("background-color");
  },

  data() {
    return {
      title: "",
      genre: null,
      categories: null,
      wordLength: null,
      genreList,
      categoryList,
      isCreating: false,
    };
  },

  methods: {
    onCreateButtonClick() {
      const { createNovel } = this;

      try {
        this.isCreating = true;
        createNovel();
      } catch (e) {
        // 特に何もしない
        console.log(e);
      } finally {
        this.isCreating = false;
      }
    },
    onSelectGenre(item) {
      this.genre = item;
    },
    onUnSelectGenre() {
      this.genre = null;
    },
    onSelectCategories(items) {
      if (items.length > 0) {
        this.categories = items;
        return;
      }

      this.categories = null;
    },

    async createNovel(isSkipStart?: boolean) {
      const { isValidWordLength } = this;

      let { title, wordLength } = this;
      if (isSkipStart) {
        title = this.$t('novelCreatePage.defaultTitle');
        wordLength = null;
      } else if (!title) {
        return;
      }

      if (!isValidWordLength) {
        const completeDialog = new Dialog(CupertinoAlertDialog);
        const data: CupertinoAlertDialogProps = {
          title: this.$t('novelCreatePage.inputErrorTitle'),
          content: this.$t('novelCreatePage.inputErrorMessage'),
          action: this.$t('common.close'),
        };
        completeDialog.show(data);
        return;
      }

      let { genre, categories } = this;

      if (isSkipStart) {
        genre = null;
        categories = null;
      } else {
        if (genre && typeof genre !== "string") {
          genre = genre.id;
        }

        const isStringArray = (array: NovelSetting[] | string[] | null) => {
          if (!array || array.length === 0) {
            return false;
          }

          return typeof array[0] === "string";
        };

        if (categories && !isStringArray(categories)) {
          categories = (categories as NovelSetting[]).map((category: NovelSetting) => category.id);
        }
      }

      const { dispatch } = this.$store;
      const { novelId } = (await dispatch("novelModule/createNovel", {
        title,
        genre,
        category: categories,
        wordLength,
        isSkip: isSkipStart,
      })) as Novel;

      if (wordLength) {
        const detail = [
          {
            name: "word-length",
            value: wordLength,
          },
        ];
        dispatch("novelThemeModule/updateTheme", { novelId, detail });
      }

      this.createManuscript(novelId);

      this.$ga.event("novel", "create-novel", "執筆をはじめる", 0);
      this.$router.push({ name: "theme", params: { novelId } });
    },
    createManuscript(novelId) {
      this.$store.dispatch("manuscriptModule/createManuscript", {
        novelId,
        title: "",
        content: "",
        manuscriptFolderId: null,
      });
    },
    onSkipStart() {
      const { createNovel } = this;

      try {
        this.isCreating = true;
        createNovel(true);
      } catch (e) {
        // 特に何もしない
        console.log(e);
      } finally {
        this.isCreating = false;
      }
    },
  },

  computed: {
    isValidWordLength() {
      const { wordLength } = this;
      const isNumber = (value: any) => !Number.isNaN(Number(value));
      if (wordLength && !isNumber(wordLength)) {
        return false;
      }

      return true;
    },
    canSubmit() {
      return this.title && this.genre !== null && this.categories !== null;
    },
    translatedGenreList() {
      return getTranslatedGenreList(this.$t);
    },
    translatedCategoryList() {
      return getTranslatedCategoryList(this.$t);
    },
  },
});
